import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  TextInput,
  Alert,
  Image,
  PermissionsAndroid,
  Platform,
  Linking
} from "react-native";
import { PermissionManager } from "../../Utils/permissions";
import React, { useEffect, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import { hp, wp } from "../../helpers/common";
import { useAuth } from "../../Utils/AuthProvider";
import { theme } from "../../constants/theme";
import ScreenWrapper from "../../Components/ScreenWrapper";
import Button from "../../Components/Button";
import Header from "../../Components/Header";
import Input from "../../Components/Input";
import { updateUser } from "../../services/userService";
import {
  getUserImageSrc,
  uploadFile,
} from "../../services/imageService";
import { Camera, User } from "../../../assets/icons";
import { launchImageLibrary, launchCamera, ImagePickerResponse } from 'react-native-image-picker';
import { validateFileSize, showFileSizeError, showFileSizeInfo } from '../../Utils/fileValidation';
import defaultUserImg from '../../../assets/images/defaultuser.png';

// Define types
interface UserData {
  name: string;
  image: any;
  bio: string;
  address: string;
}

interface ImageAsset {
  uri: string;
  type: string;
  fileName?: string;
}

const EditProfile = () => {
  const { userData, setUserData } = useAuth();
  const navigation = useNavigation<any>();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [user, setUser] = useState<UserData>({
    name: "",
    image: null,
    bio: "",
    address: "",
  });

  useEffect(() => {
    if (userData) {
      setUser({
        name: userData.name || "",
        image: userData.image || null,
        address: userData.address || "",
        bio: userData.bio || "",
      });
    }
  }, [userData]);

  // Debug effect to monitor user state changes
  useEffect(() => {
    // User state changed
  }, [user]);

  const requestCameraPermission = async () => {
    const result = await PermissionManager.requestCameraPermission();
    return result.granted;
  };

  const requestGalleryPermission = async () => {
    const result = await PermissionManager.requestMediaPermissions();
    return result.granted;
  };

  const onPickImage = async () => {
    // On web, skip the native Alert/action sheet and open gallery directly
    if (Platform.OS === 'web') {
      pickImageFromGallery();
      return;
    }

    Alert.alert(
      "Select Image",
      "Choose an option",
      [
        {
          text: "Camera",
          onPress: () => openCamera(),
        },
        {
          text: "Gallery",
          onPress: () => openGallery(),
        },
        {
          text: "Cancel",
          style: "cancel",
        },
      ]
    );
  };

  const openCamera = async () => {
    const hasPermission = await requestCameraPermission();
    if (!hasPermission) {
      Alert.alert("Permission Denied", "Camera permission is required to take photos");
      return;
    }

    setImageLoading(true);
    const options = {
      mediaType: 'photo',
      includeBase64: false,
      maxHeight: 2000,
      maxWidth: 2000,
      quality: 0.8,
      saveToPhotos: false,
    };

    launchCamera(options as any, (response: ImagePickerResponse) => {
      setImageLoading(false);
      if (response.didCancel) {
        // User cancelled camera
      } else if (response.errorCode) {
        Alert.alert("Error", "Failed to take photo");
      } else if (response.assets && response.assets[0]) {
        const imageAsset = response.assets[0];
        if (imageAsset.uri) {
          const newImage = {
            uri: imageAsset.uri,
            type: imageAsset.type || 'image/jpeg',
            fileName: imageAsset.fileName || `photo_${Date.now()}.jpg`,
          };
          setTimeout(() => {
            // Use functional update to avoid stale state issues
            setUser(prev => ({
              ...prev,
              image: newImage,
            }));
            setImageKey(prev => prev + 1);
          }, 100);
        }
      }
    });
  };

  const pickImageFromGallery = async () => {
    setImageLoading(true);
    const options = {
      mediaType: 'photo',
      includeBase64: false,
      maxHeight: 2000,
      maxWidth: 2000,
      quality: 0.8,
    };

    try {
      // Use Promise-based API (required for web shim)
      const result = await launchImageLibrary(options as any);
      setImageLoading(false);

      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert("Error", "Failed to select photo");
        return;
      }
      if (!result.assets || !result.assets[0]) return;

      const imageAsset = result.assets[0];
      if (!imageAsset.uri) return;

      // Validate file size before proceeding (supports web & native)
      const fileSize = (imageAsset as any).fileSize as number | undefined;
      const sizeValidation = await validateFileSize(imageAsset.uri, fileSize);
      if (!sizeValidation.isValid) {
        showFileSizeError(sizeValidation.errorMessage || 'File size validation failed');
        return;
      }

      // Show file size info in console
      showFileSizeInfo(sizeValidation.size);

      const newImage = {
        uri: imageAsset.uri,
        type: imageAsset.type || 'image/jpeg',
        fileName: imageAsset.fileName || `photo_${Date.now()}.jpg`,
      };
      setTimeout(() => {
        // Use functional update to avoid stale state issues
        setUser(prev => ({ ...prev, image: newImage }));
        setImageKey(prev => prev + 1);
      }, 100);
    } catch (error) {
      setImageLoading(false);
      console.error('Error picking image from gallery:', error);
      Alert.alert('Error', 'Failed to select photo');
    }
  };

  const openGallery = async () => {
    const androidVersion = Platform.OS === 'android' ? Platform.Version : 0;
    if (Platform.OS !== 'android' || androidVersion >= 33) {
      pickImageFromGallery();
      return;
    }
    const hasPermission = await requestGalleryPermission();
    if (hasPermission) pickImageFromGallery();
  };

  const onSubmit = async () => {
    let updatedUserData = { ...user };
    let { name, address, image, bio } = updatedUserData;
    
    if (!name || !address || !bio) {
      Alert.alert("Profile", "Please fill all the fields");
      return;
    }

    // Make image optional - use existing image if no new one is selected
    if (!image && !userData?.image) {
      Alert.alert("Profile", "Please choose a profile image");
      return;
    }

    setLoading(true);
    
    // Handle image upload
    if (typeof image == "object" && image?.uri) {
      let imageResult = await uploadFile('uploads', image.uri, true, image.fileName, 'profiles');
      
      if (imageResult.success) {
        updatedUserData.image = imageResult.data;
        // Update local user state with the new image path
        setUser(prev => ({
          ...prev,
          image: imageResult.data
        }));
      } else {
        updatedUserData.image = userData?.image || null;
        Alert.alert("Image Upload Failed", imageResult.msg || "Failed to upload image");
        setLoading(false);
        return;
      }
    } else if (!image) {
      // Keep existing image if no new image is selected
      updatedUserData.image = userData?.image || null;
    }

    const res = await updateUser(userData?.id, updatedUserData);
    
    setLoading(false);
    if (res.success) {
      setUserData({ ...userData, ...updatedUserData });
      navigation.goBack();
    } else {
      Alert.alert("Profile", (res as any).msg || "Something went wrong");
    }
  };

  // Show loading if userData is not yet loaded
  if (!userData) {
    return (
      <ScreenWrapper bg="white">
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ color: theme.colors.textLight }}>Loading profile...</Text>
        </View>
      </ScreenWrapper>
    );
  }

  // Handle image source
  const getImageSource = () => {
    // Check if we have a newly selected image (object with uri) - for preview before upload
    if (user.image && typeof user.image === "object" && user.image.uri) {
      return { uri: user.image.uri };
    }
    
    // Check if we have a string image path from userData (updated from server)
    if (userData?.image && typeof userData.image === "string") {
      return getUserImageSrc(userData.image);
    }
    
    // Check if we have a string image path from local user state (just uploaded)
    if (user.image && typeof user.image === "string") {
      return getUserImageSrc(user.image);
    }
    
    // Fallback to default image
    return defaultUserImg;
  };

  return (
    <ScreenWrapper bg="white">
      <View style={styles.container}>
        <ScrollView style={{ flex: 1 }}>
          <Header title="Edit Profile" />

          {/* form */}
          <View style={styles.form}>
            <View style={styles.avatarContainer}>
              <View style={styles.avatar}>
                <Image 
                  key={`${imageKey}-${user.image ? (typeof user.image === 'object' ? user.image.uri : user.image) : 'default'}`}
                  source={getImageSource()} 
                  style={styles.avatarImage}
                  defaultSource={defaultUserImg}
                  resizeMode="cover"
                />
              </View>
              <Pressable style={styles.cameraIcon} onPress={onPickImage} disabled={imageLoading}>
                {imageLoading ? (
                  <Text style={{ fontSize: 16, color: theme.colors.textLight }}>...</Text>
                ) : (
                  <Camera 
                    strokeWidth={2.5} 
                    size={20} 
                    color={user.image && typeof user.image === "object" ? theme.colors.primary : theme.colors.textLight} 
                  />
                )}
              </Pressable>
              {user.image && typeof user.image === "object" && (
                <Text style={styles.imageSelectedText}>✓</Text>
              )}
              {imageLoading && (
                <View style={styles.imageLoadingOverlay}>
                  <Text style={styles.imageLoadingText}>Processing...</Text>
                </View>
              )}
            </View>
            <Text style={{ fontSize: hp(1.5), color: theme.colors.text }}>
              Please fill your profile details
            </Text>
            <Input
              icon={<User size={26} />}
              placeholder="Enter your name"
              placeholderTextColor={theme.colors.textLight}
              value={user.name}
              onChangeText={(value) => setUser({ ...user, name: value })}
            />
            <Input
              icon={<Text style={{fontSize: 20, color: theme.colors.textLight}}>📍</Text>}
              placeholder="Enter your city"
              placeholderTextColor={theme.colors.textLight}
              value={user.address}
              onChangeText={(value) => setUser({ ...user, address: value })}
            />

            <Input
              placeholder="Enter your bio"
              placeholderTextColor={theme.colors.textLight}
              onChangeText={(value) => setUser({ ...user, bio: value })}
              multiline={true}
              value={user.bio}
              containerStyle={styles.bio}
            />

            {/* button */}
            <Button title="Update" loading={loading} onPress={onSubmit} />
          </View>
        </ScrollView>
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: wp(4),
  },
  avatarContainer: {
    height: hp(14),
    width: hp(14),
    alignSelf: "center",
    position: "relative",
  },
  avatar: {
    width: "100%",
    height: "100%",
    borderRadius: theme.radius.xxl * 1.8,
    borderWidth: 1,
    borderColor: theme.colors.darkLight,
    backgroundColor: theme.colors.gray,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    borderRadius: theme.radius.xxl * 1.8,
  },
  avatarText: {
    fontSize: hp(4),
    fontWeight: "bold",
    color: theme.colors.text,
  },
  cameraIcon: {
    position: "absolute",
    bottom: 0,
    right: -10,
    padding: 8,
    borderRadius: 50,
    backgroundColor: "white",
    shadowColor: theme.colors.textLight,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 5,
    elevation: 7,
  },
  form: {
    gap: 18,
    marginTop: 20,
  },
  bio: {
    flexDirection: "row",
    height: hp(15),
    alignItems: "flex-start",
    paddingVertical: 15,
  },
  imageSelectedText: {
    position: "absolute",
    top: 10,
    right: 10,
    fontSize: 16,
    color: theme.colors.primary,
    fontWeight: "bold",
  },
  imageLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.radius.xxl * 1.8,
  },
  imageLoadingText: {
    color: "white",
    fontSize: 12,
    fontWeight: "bold",
  },
});

export default EditProfile;

// Alternative image picker implementation using document picker
// Uncomment this if react-native-image-picker continues to have issues
/*
import DocumentPicker from 'react-native-document-picker';

const openDocumentPicker = async () => {
  try {
    const result = await DocumentPicker.pick({
      type: [DocumentPicker.types.images],
    });
    
    if (result && result[0]) {
      const file = result[0];
      setUser({
        ...user,
        image: {
          uri: file.uri,
          type: file.type || 'image/jpeg',
          fileName: file.name || `photo_${Date.now()}.jpg`,
        },
      });
    }
  } catch (err) {
    if (!DocumentPicker.isCancel(err)) {
      Alert.alert("Error", "Failed to select image");
    }
  }
};
*/
